import * as PIXI from "pixi.js";
import {config} from "../../../config";
import {PredefinedPIXIAnchorPoint} from "../../../PredefinedPIXIAnchorPoint";
import data from "../../configs/data";
import {Button} from "../../controlElements/Button";
import {CustomSpineAnimation} from "../../CustomSpineAnimation";
import {IBetPicker} from "./IBetPicker";

export class BetPicker extends PIXI.Container implements IBetPicker {

    private readonly ANIMATION_CONFIG = {
        NAME: "betPicker",
        SMALL_BETS_NAME: "smallBetsBetPicker",
        EXTREMELY_SMALL_BETS_NAME: "extremelySmallBetsBetPicker",
        STATES: {
            IDLE: "idle",
            DOWN_0_1: "0_1_down",
            UP_0_1: "0_1_up",
            DOWN_0_5: "0_5_down",
            UP_0_5: "0_5_up",
            DOWN_100: "100_down",
            UP_100: "100_up",
            DOWN_10: "10_down",
            UP_10: "10_up",
            DOWN_1: "1_down",
            UP_1: "1_up",
            DOWN_25: "25_down",
            UP_25: "25_up",
            DOWN_2_5: "2_5_down",
            UP_2_5: "2_5_up",
            DOWN_5: "5_down",
            UP_5: "5_up",
            DOWN_CLEAR_CHIPS: "clear_chips_down",
            UP_CLEAR_CHIPS: "clear_chips_up",
        },
    };

    private betPickerAnimation: CustomSpineAnimation;
    private subscribers: Array<Function> = [];
    private previousButton: Button;
    private nextButton: Button;
    private invisiblePreviousButton: Button;
    private invisibleNextButton: Button;

    private currentBetPickerValueIndex = 3;

    private readonly betPickerValues = [-1, 0.1, 0.5, 1, 2.5, 5, 10, 25, 50];
    private readonly BACKGROUND_POSITION = new PIXI.Point(0, 0);
    private readonly PREVIOUS_BUTTON_POSITION = new PIXI.Point(-165, 3);
    private readonly NEXT_BUTTON_POSITION = new PIXI.Point(165, 3);
    private readonly INVISIBLE_PREVIOUS_BUTTON_POSITION = new PIXI.Point(-122, 0);
    private readonly INVISIBLE_NEXT_BUTTON_POSITION = new PIXI.Point(122, 0);

    constructor() {
        super();

        if (config.extremelySmallBets) {
            this.betPickerValues = [-1, 0.001, 0.005, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5];
        } else if (config.smallBets) {
            this.betPickerValues = [-1, 0.01, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5];
        } else {
            this.betPickerValues = [-1, 0.1, 0.5, 1, 2.5, 5, 10, 25, 50];
        }

        this.createAndAddBackground();
        this.createAndAddBetPickerAnimation();
        this.createAndAddInvisiblePreviousButton();
        this.createAndAddInvisibleNextButton();
        this.createAndAddPreviousButton();
        this.createAndAddNextButton();
        this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.IDLE);
    }

    public subscribeOnBetChanged(subscriber: Function): void {
        this.subscribers.push(subscriber);
    }

    public getBetValue(): number {
        return this.betPickerValues[this.currentBetPickerValueIndex];
    }

    private onValueChosen(): void {
        for (let i = 0; i < this.subscribers.length; i++) {
            this.subscribers[i](this.betPickerValues[this.currentBetPickerValueIndex]);
        }
    }

    private onNextButtonClicked(): void {
        this.currentBetPickerValueIndex++;
        if (this.currentBetPickerValueIndex === this.betPickerValues.length) {
            this.currentBetPickerValueIndex = 0;
        }
        this.disableAllButtons();
        this.playAnimationOnNextBetChosen(this.currentBetPickerValueIndex);
        this.onValueChosen();
    }

    private onPreviousButtonClicked(): void {
        this.currentBetPickerValueIndex--;
        if (this.currentBetPickerValueIndex < 0) {
            this.currentBetPickerValueIndex = this.betPickerValues.length - 1;
        }
        this.disableAllButtons();
        this.playAnimationOnPreviousBetChosen(this.currentBetPickerValueIndex);
        this.onValueChosen();
    }

    private enableAllButtons(): void {
        this.previousButton.enabled = true;
        this.nextButton.enabled = true;
        this.invisiblePreviousButton.enabled = true;
        this.invisibleNextButton.enabled = true;
    }

    private disableAllButtons(): void {
        this.previousButton.enabled = false;
        this.nextButton.enabled = false;
        this.invisiblePreviousButton.enabled = false;
        this.invisibleNextButton.enabled = false;
    }

    private playAnimationOnNextBetChosen(newBetIndex: number): void {
        switch (newBetIndex) {
            case 0:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_CLEAR_CHIPS);
                break;
            case 1:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_0_1);
                break;
            case 2:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_0_5);
                break;
            case 3:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_1);
                break;
            case 4:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_2_5);
                break;
            case 5:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_5);
                break;
            case 6:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_10);
                break;
            case 7:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_25);
                break;
            case 8:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.DOWN_100);
                break;
            default:
                throw new Error("Illegal betIndex send!");
        }
    }

    private playAnimationOnPreviousBetChosen(newBetIndex: number): void {
        switch (newBetIndex) {
            case 0:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_CLEAR_CHIPS);
                break;
            case 1:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_0_1);
                break;
            case 2:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_0_5);
                break;
            case 3:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_1);
                break;
            case 4:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_2_5);
                break;
            case 5:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_5);
                break;
            case 6:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_10);
                break;
            case 7:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_25);
                break;
            case 8:
                this.betPickerAnimation.play(this.ANIMATION_CONFIG.STATES.UP_100);
                break;
            default:
                throw new Error("Illegal betIndex send!");
        }
    }

    private onAnimationEnd(): void {
        this.enableAllButtons();
    }

    private createAndAddInvisiblePreviousButton(): void {
        this.invisiblePreviousButton = new Button(data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.audios.betValueSmaller);
        this.invisiblePreviousButton.position = this.INVISIBLE_PREVIOUS_BUTTON_POSITION;
        this.invisiblePreviousButton.subscribeOnClicked(this.onPreviousButtonClicked.bind(this));
        this.addChild(this.invisiblePreviousButton);
    }

    private createAndAddInvisibleNextButton(): void {
        this.invisibleNextButton = new Button(data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.images.invisibleBetPickerButton,
            data.images.invisibleBetPickerButton, data.audios.betValueBigger);
        this.invisibleNextButton.position = this.INVISIBLE_NEXT_BUTTON_POSITION;
        this.invisibleNextButton.subscribeOnClicked(this.onNextButtonClicked.bind(this));
        this.addChild(this.invisibleNextButton);
    }

    private createAndAddPreviousButton(): void {
        this.previousButton = new Button(data.images.betPickerArrowLeftBase,
            data.images.betPickerArrowLeftHover, data.images.betPickerArrowLeftClick,
            data.images.betPickerArrowLeftInActive, data.audios.betValueSmaller);
        this.previousButton.position = this.PREVIOUS_BUTTON_POSITION;
        this.previousButton.subscribeOnClicked(this.onPreviousButtonClicked.bind(this));
        this.addChild(this.previousButton);
    }

    private createAndAddNextButton(): void {
        this.nextButton = new Button(data.images.betPickerArrowRightBase,
            data.images.betPickerArrowRightHover, data.images.betPickerArrowRightClick,
            data.images.betPickerArrowRightInActive, data.audios.betValueBigger);
        this.nextButton.position = this.NEXT_BUTTON_POSITION;
        this.nextButton.subscribeOnClicked(this.onNextButtonClicked.bind(this));
        this.addChild(this.nextButton);
    }

    private createAndAddBetPickerAnimation(): void {
        const container = new PIXI.Container();
        this.addMaskToBetPickerAnimation(container);
        if (config.extremelySmallBets) {
            this.betPickerAnimation = new CustomSpineAnimation(this.ANIMATION_CONFIG.EXTREMELY_SMALL_BETS_NAME);
        } else if (config.smallBets) {
            this.betPickerAnimation = new CustomSpineAnimation(this.ANIMATION_CONFIG.SMALL_BETS_NAME);
        } else {
            this.betPickerAnimation = new CustomSpineAnimation(this.ANIMATION_CONFIG.NAME);
        }
        this.betPickerAnimation.addOnCompleteListener(this.onAnimationEnd.bind(this));
        container.addChild(this.betPickerAnimation);
        this.addChild(container);
    }

    private createAndAddBackground(): void {
        const background: PIXI.Sprite = PIXI.Sprite.fromImage(data.images.betPickerPanelBackground);
        background.anchor = PredefinedPIXIAnchorPoint.CENTER;
        background.position = this.BACKGROUND_POSITION;
        this.addChild(background);
    }

    private addMaskToBetPickerAnimation(container: PIXI.Container): void {
        const mask = new PIXI.Graphics();
        mask.beginFill(0xFFFFFF);
        mask.drawRect(-130, -50, 260, 100);
        mask.endFill();
        container.mask = mask;
        container.addChild(mask);
    }
}



// WEBPACK FOOTER //
// ./src/app/userInterface/betPicker/BetPicker.ts